import type { ReactNode } from 'react'

/**
 * `div.steps` → `step-rail` (process/08-COMPONENT-CATALOGUE.md). "The progress rail across the
 * top of a wizard machine." gap 8px → --sp-4 (0 delta); padding 0 22px 14px → --sp-10 (+2px) /
 * --sp-7 (0 delta); border-bottom width not a token family.
 */
export function StepRail({ children }: { children: ReactNode }) {
  return (
    <div data-cmp="step-rail" className="flex items-center justify-center gap-4 pb-7 px-10 border-b border-bd">
      {children}
    </div>
  )
}

/**
 * `div.step` → `step` (`current`/`done` variants). gap 8px → --sp-4; font-size 12.5px →
 * --fs-body-lg (+0.5px delta). The ordinal (`span.n`) is uncatalogued (§08's own tail finding —
 * shared with `stat`'s figure), so it carries no `data-cmp` of its own.
 */
export function Step({ ordinal, label, state }: { ordinal: number; label: string; state: 'pending' | 'current' | 'done' }) {
  const numberClass =
    state === 'current' ? 'bg-blue text-white' : state === 'done' ? 'bg-pill-ok text-white' : 'bg-row text-tx2'
  return (
    <div
      data-cmp="step"
      className={`flex items-center gap-4 text-body-lg ${state === 'current' ? 'text-blue font-semibold' : 'text-tx2'}`}
    >
      <span className={`w-5 h-5 rounded-pill grid place-items-center text-small font-heavy ${numberClass}`}>{ordinal}</span>
      {label}
    </div>
  )
}

/** `span.bar`'s styling, for a caller to spread onto its own connector element. Not exported as
 * a component: `span.bar` is catalogued as "No component" (§08 — "the connector line between two
 * `step`s in a `step-rail`"), and a function under `src/components/` that never carries
 * `data-cmp` is exactly what `data-cmp-required` exists to catch — correctly, here, since this
 * really is deliberately uncatalogued rather than an oversight. 36px is above the space scale's
 * top step and is layout, per §33.9's own rule — a literal, not a token exception to invent. */
export const STEP_BAR_STYLE = { width: '36px', height: '1px' } as const
export const STEP_BAR_CLASSNAME = 'bg-bd2'
